import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PawPrint, MapPin, Globe, Phone, Plus, Search, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const blank = { name: "", category: "Trainer", description: "", location: "", website: "", phone: "", image: "", contact_email: "" };

export default function Directory() {
  const { user } = useAuth();
  const [cats, setCats] = useState(["All"]);
  const [cat, setCat] = useState("All");
  const [listings, setListings] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);

  const load = () => api.get(`/listings${cat !== "All" ? `?category=${encodeURIComponent(cat)}` : ""}`).then((r) => setListings(r.data)).catch(() => {});
  useEffect(() => { api.get("/directory/categories").then((r) => setCats(["All", ...r.data])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [cat]); // eslint-disable-line

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Business name is required"); return; }
    try { await api.post("/listings/submit", f); toast.success("Submitted! We'll review and add it soon."); setOpen(false); setF(blank); }
    catch { toast.error("Could not submit"); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div data-testid="directory-page" className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 backdrop-blur bg-background/80 border-b border-border/60">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 h-16">
          <Link to={user ? "/" : "/"} className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground"><PawPrint className="h-5 w-5" /></div>
            <span className="font-head font-extrabold text-lg tracking-tight">My Pack</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? <Link to="/" data-testid="dir-dashboard" className="text-sm font-medium hover:text-primary flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
              : <Link to="/login" className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold">Log in</Link>}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-head text-4xl font-extrabold tracking-tight">Pet Services Directory</h1>
            <p className="text-muted-foreground mt-2">Find trusted trainers, vets, groomers and more.</p>
          </div>
          <Button data-testid="list-business" onClick={() => setOpen(true)} className="rounded-full"><Plus className="h-4 w-4 mr-1" /> List your business — free</Button>
        </div>

        <div className="flex gap-2 overflow-x-auto scroll-thin pb-2 mb-6">
          {cats.map((c) => (
            <button key={c} data-testid={`dir-cat-${c}`} onClick={() => setCat(c)} className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>{c}</button>
          ))}
        </div>

        {listings.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-16 text-center bg-card/50">
            <Search className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No listings here yet. Be the first — list your business free!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map((l, i) => (
              <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                data-testid={`listing-${l.id}`} className="rounded-2xl bg-card border border-border/70 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="h-36 bg-muted relative">
                  {l.image ? <img src={l.image} alt={l.name} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><PawPrint className="h-8 w-8 text-muted-foreground/40" /></div>}
                  {l.featured && <span className="absolute top-3 right-3 text-xs bg-secondary text-white px-2 py-0.5 rounded-full">Featured</span>}
                </div>
                <div className="p-5">
                  <span className="text-xs text-accent-foreground bg-accent/20 px-2 py-0.5 rounded-full">{l.category}</span>
                  <h3 className="font-head font-semibold text-lg mt-2">{l.name}</h3>
                  {l.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.description}</p>}
                  <div className="flex flex-col gap-1 mt-3 text-sm text-muted-foreground">
                    {l.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{l.location}</span>}
                    {l.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{l.phone}</span>}
                    {l.website && <a href={l.website.startsWith("http") ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-primary hover:underline"><Globe className="h-3.5 w-3.5" />Visit website</a>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto scroll-thin">
          <DialogHeader><DialogTitle className="font-head">List your business — free</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2 mb-2">Submit your details and we'll review and publish your listing.</p>
          <div className="space-y-3">
            <div><Label>Business name *</Label><Input data-testid="biz-name" value={f.name} onChange={set("name")} /></div>
            <div><Label>Category</Label>
              <select data-testid="biz-category" value={f.category} onChange={set("category")} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
                {cats.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Description</Label><Textarea data-testid="biz-desc" value={f.description} onChange={set("description")} placeholder="What you offer..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Location</Label><Input data-testid="biz-location" value={f.location} onChange={set("location")} placeholder="City / area" /></div>
              <div><Label>Phone</Label><Input data-testid="biz-phone" value={f.phone} onChange={set("phone")} /></div>
            </div>
            <div><Label>Website</Label><Input data-testid="biz-website" value={f.website} onChange={set("website")} placeholder="https://..." /></div>
            <div><Label>Image URL</Label><Input data-testid="biz-image" value={f.image} onChange={set("image")} placeholder="https://..." /></div>
            <div><Label>Contact email</Label><Input data-testid="biz-email" type="email" value={f.contact_email} onChange={set("contact_email")} /></div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="submit-listing" onClick={submit} className="rounded-full">Submit listing</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
